import React, { useState, useEffect } from 'react';
import { admin } from '../../services/api';
import '../../styles/AdminMedicationList.css';
import AddMedicationModal from './AddMedicationModal';
import DailyMedSearchModal from './DailyMedSearchModal';
import BulkImportModal from './BulkImportModal';
import BulkDeleteModal from './BulkDeleteModal';

function MedicationList() {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    reviewer: 'all',
    stuckOnly: false,
    dateFrom: '',
    dateTo: '',
    scrapeStatus: 'all',
  });
  const [sortBy, setSortBy] = useState('date_desc');
  const [reviewers, setReviewers] = useState([]);

  useEffect(() => {
    fetchMedications();
    fetchReviewers();
  }, [filters, sortBy]);

  const fetchMedications = async () => {
    setLoading(true);
    try {
      const params = {
        sort_by: sortBy,
      };
      
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      
      if (filters.reviewer !== 'all') {
        params.reviewer = filters.reviewer;
      }
      
      if (filters.stuckOnly) {
        params.stuck_only = true;
      }
      
      if (filters.dateFrom) {
        params.date_from = filters.dateFrom;
      }
      
      if (filters.dateTo) {
        params.date_to = filters.dateTo;
      }
      
      if (filters.scrapeStatus !== 'all') {
        params.scrape_status = filters.scrapeStatus;
      }

      const response = await admin.getMedications(params);
      setMedications(response.data || []);
    } catch (err) {
      console.error('Failed to fetch medications:', err);
      // Show placeholder data if endpoint doesn't exist
      if (err.response?.status === 404) {
        alert('Admin medication endpoint not yet implemented. Showing placeholder.');
        setMedications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewers = async () => {
    try {
      const response = await admin.getUsers({ role: 'pharmacist' });
      setReviewers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch reviewers:', err);
    }
  };

  const handleForceRelease = async (id, medicationName) => {
    if (window.confirm(`Force release "${medicationName}"? This will free it for another pharmacist.`)) {
      try {
        await admin.forceRelease(id);
        alert('Medication released successfully!');
        fetchMedications();
      } catch (err) {
        console.error('Failed to release:', err);
        alert('Force release failed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleDelete = async (id, medicationName) => {
    if (window.confirm(`DELETE "${medicationName}" permanently? This cannot be undone!`)) {
      const confirm2 = window.prompt('Type "DELETE" to confirm:');
      if (confirm2 && confirm2.toUpperCase() === 'DELETE') {
        try {
          await admin.deleteMedication(id);
          alert('Medication deleted successfully!');
          fetchMedications();
        } catch (err) {
          console.error('Failed to delete:', err);
          alert('Delete failed: ' + (err.response?.data?.detail || err.message));
        }
      } else if (confirm2 !== null) {
        alert('Delete cancelled. You must type "DELETE" exactly (case-insensitive).');
      }
    }
  };

  const handleSimplify = async (id, medicationName) => {
    if (!window.confirm(`Process "${medicationName}"?\n\nThis will:\n1. Fetch FDA data from DailyMed\n2. Simplify all sections via AI\n3. Validate required fields\n\nThis may take several minutes.`)) {
      return;
    }
    
    try {
      const response = await admin.simplifyMedication(id);
      alert(`${response.data.message}\n\nStatus: ${response.data.status}`);
      fetchMedications();
    } catch (err) {
      console.error('Failed to process:', err);
      alert('Processing failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSendToReview = async (id, medicationName) => {
    if (!window.confirm(`Send "${medicationName}" to pharmacist review?`)) {
      return;
    }
    
    try {
      await admin.sendToReview(id);
      alert('Sent to review successfully!');
      fetchMedications();
    } catch (err) {
      console.error('Failed to send to review:', err);
      alert('Send failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      // Processing pipeline statuses
      queued: { label: 'Queued', class: 'status-queued' },
      fetching: { label: 'Fetching FDA Data', class: 'status-fetching' },
      simplifying: { label: 'Simplifying', class: 'status-simplifying' },
      validating: { label: 'Validating', class: 'status-validating' },
      simplify_failed: { label: 'Failed', class: 'status-failed' },
      
      // Review statuses
      review_ready: { label: 'Ready for Review', class: 'status-review-ready' },
      available: { label: 'Available', class: 'status-available' },
      claimed: { label: 'Claimed', class: 'status-claimed' },
      in_review: { label: 'In Review', class: 'status-review' },
      approved: { label: 'Approved', class: 'status-approved' },
      published: { label: 'Published', class: 'status-published' },
    };
    
    const statusInfo = statusMap[status] || { label: status, class: '' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateProgress = (medication) => {
    if (!medication.sections) return 'N/A';
    const approved = medication.sections.filter(s => s.approved).length;
    return `${approved}/${medication.sections.length}`;
  };

  const isStuck = (claimedAt) => {
    if (!claimedAt) return false;
    const hoursSince = (Date.now() - new Date(claimedAt).getTime()) / (1000 * 60 * 60);
    return hoursSince > 24;
  };

  if (loading) {
    return <div className="loading">Loading medications...</div>;
  }

  return (
    <div className="admin-medication-list">
      <div className="filters-section">
        <h3>Filters & Sort</h3>
        <div className="filters-row">
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filters.status} 
              onChange={(e) => setFilters({...filters, status: e.target.value})}
            >
              <option value="all">All Statuses</option>
              <option value="queued">Queued</option>
              <option value="fetching">Fetching FDA Data</option>
              <option value="simplifying">Simplifying</option>
              <option value="validating">Validating</option>
              <option value="simplify_failed">Failed</option>
              <option value="review_ready">Ready for Review</option>
              <option value="available">Available</option>
              <option value="claimed">Claimed</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Reviewer:</label>
            <select 
              value={filters.reviewer} 
              onChange={(e) => setFilters({...filters, reviewer: e.target.value})}
            >
              <option value="all">All Reviewers</option>
              {reviewers.map(reviewer => (
                <option key={reviewer.id} value={reviewer.id}>
                  {reviewer.full_name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>
              <input 
                type="checkbox" 
                checked={filters.stuckOnly}
                onChange={(e) => setFilters({...filters, stuckOnly: e.target.checked})}
              />
              Show Stuck Only (>24h)
            </label>
          </div>

          <div className="filter-group">
            <label>Date From:</label>
            <input 
              type="date" 
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
            />
          </div>

          <div className="filter-group">
            <label>Date To:</label>
            <input 
              type="date" 
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
            />
          </div>

          <div className="filter-group">
            <label>Scrape Status:</label>
            <select 
              value={filters.scrapeStatus}
              onChange={(e) => setFilters({...filters, scrapeStatus: e.target.value})}
            >
              <option value="all">All</option>
              <option value="failed">Failed</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_desc">Date (Newest)</option>
              <option value="date_asc">Date (Oldest)</option>
              <option value="status">Status</option>
              <option value="reviewer">Reviewer</option>
            </select>
          </div>
        </div>
      </div>

      <div className="action-buttons" style={{ marginBottom: '16px', display: 'flex', gap: '10px' }}>
        <button type="button" className="btn-primary" onClick={() => setShowSearchModal(true)}>
          🔍 Search DailyMed
        </button>
        <button type="button" className="btn-secondary" onClick={() => setShowAddModal(true)}>
          ➕ Add Medication
        </button>
        <button type="button" className="btn-secondary" onClick={() => setShowBulkModal(true)}>
          📦 Bulk Import
        </button>
        {selectedIds.length > 0 && (
          <button 
            type="button" 
            className="btn-danger"
            onClick={() => setShowBulkDeleteModal(true)}
            disabled={selectedIds.length === 0 || selectedIds.length > 500}
          >
            🗑️ Delete ({selectedIds.length} selected)
          </button>
        )}
      </div>

      <div className="medications-table-container">
        <table className="medications-table">
          <thead>
            <tr>
              <th style={{width: '40px'}}>
                <input 
                  type="checkbox"
                  checked={selectedIds.length === medications.length && medications.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(medications.map(m => m.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                />
              </th>
              <th>Name</th>
              <th>Status</th>
              <th>Reviewer</th>
              <th>Claimed At</th>
              <th>Progress</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {medications.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No medications found. {filters.status !== 'all' || filters.reviewer !== 'all' || filters.stuckOnly ? 'Try adjusting filters.' : ''}
                </td>
              </tr>
            ) : (
              medications.map(med => (
                <tr key={med.id} className={isStuck(med.claimed_at) ? 'stuck-row' : ''}>
                  <td>
                    <input 
                      type="checkbox"
                      checked={selectedIds.includes(med.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds([...selectedIds, med.id]);
                        } else {
                          setSelectedIds(selectedIds.filter(id => id !== med.id));
                        }
                      }}
                    />
                  </td>
                  <td className="med-name">
                    {med.name}
                    {isStuck(med.claimed_at) && <span className="stuck-badge">⚠️ STUCK</span>}
                  </td>
                  <td>{getStatusBadge(med.status)}</td>
                  <td>{med.reviewer_name || '-'}</td>
                  <td>{formatDateTime(med.claimed_at)}</td>
                  <td>{calculateProgress(med)}</td>
                  <td className="actions-cell">
                    {med.status === 'claimed' && (
                      <button 
                        type="button"
                        className="btn-small btn-warning"
                        onClick={() => handleForceRelease(med.id, med.name)}
                      >
                        Force Release
                      </button>
                    )}
                    {(med.status === 'queued' || med.status === 'simplify_failed') && (
                      <button 
                        type="button"
                        className="btn-small btn-primary"
                        onClick={() => handleSimplify(med.id, med.name)}
                      >
                        {med.status === 'simplify_failed' ? 'Retry' : 'Process'}
                      </button>
                    )}
                    {(med.status === 'fetching' || med.status === 'simplifying' || med.status === 'validating') && (
                      <span className="btn-small" style={{background: '#fff3cd', padding: '4px 8px'}}>
                        {med.status === 'fetching' ? 'Fetching FDA...' : med.status === 'simplifying' ? 'Simplifying...' : 'Validating...'}
                      </span>
                    )}
                    {(med.status === 'review_ready' || med.status === 'available') && (
                      <span className="btn-small" style={{background: '#d4edda', padding: '4px 8px', color: '#155724'}}>
                        Ready for Review
                      </span>
                    )}
                    <button 
                      type="button"
                      className="btn-small btn-danger"
                      onClick={() => handleDelete(med.id, med.name)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddMedicationModal
          show={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            fetchMedications();
          }}
        />
      )}

      {showBulkModal && (
        <BulkImportModal
          show={showBulkModal}
          onClose={() => {
            setShowBulkModal(false);
            fetchMedications();
          }}
        />
      )}

      {showSearchModal && (
        <DailyMedSearchModal
          show={showSearchModal}
          onClose={() => {
            setShowSearchModal(false);
            fetchMedications();
          }}
          onBulkAdd={() => {
            setShowSearchModal(false);
            fetchMedications();
          }}
        />
      )}

      {showBulkDeleteModal && (
        <BulkDeleteModal
          show={showBulkDeleteModal}
          selectedIds={selectedIds}
          onClose={() => {
            setShowBulkDeleteModal(false);
          }}
          onDeleteComplete={() => {
            setShowBulkDeleteModal(false);
            setSelectedIds([]);
            fetchMedications();
          }}
        />
      )}
    </div>
  );
}

export default MedicationList;
